/**
 * BottomSheet - Mobile-friendly slide-up panel
 *
 * Core component: No internal dependencies.
 * Used by: SelectBottomSheet, MenuButton, InfoCardDetail
 */

import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

export interface BottomSheetProps {
  /** Whether the sheet is open */
  isOpen: boolean;
  /** Callback when sheet should close */
  onClose: () => void;
  /** Sheet title */
  title?: string;
  /** Sheet content */
  children: React.ReactNode;
  /** Height: 'auto' fits content, 'half' takes half screen, 'full' takes most of screen */
  height?: 'auto' | 'half' | 'full';
}

export function BottomSheet({
  isOpen,
  onClose,
  title,
  children,
  height = 'auto',
}: BottomSheetProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      requestAnimationFrame(() => setIsAnimating(true));
      return;
    }
    setIsAnimating(false);
    const timer = setTimeout(() => setIsVisible(false), 300);
    return () => clearTimeout(timer);
  }, [isOpen]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isVisible) return null;

  const heightClass = {
    auto: 'max-h-[80vh]',
    half: 'h-[50vh]',
    full: 'h-[90vh]',
  }[height];

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop - click to close */}
      <div
        className={cn(
          'absolute inset-0 bg-black/50 transition-opacity duration-300',
          isAnimating ? 'opacity-100' : 'opacity-0'
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Sheet */}
      <div
        className={cn(
          'absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl',
          'transition-transform duration-300 ease-out',
          'flex flex-col',
          heightClass,
          isAnimating ? 'translate-y-0' : 'translate-y-full'
        )}
      >
        {/* Title - centered, subtle */}
        {title && (
          <div className="pt-5 pb-2 flex-shrink-0">
            <h3 className="text-sm font-medium text-slate-400 text-center">{title}</h3>
          </div>
        )}

        {/* Content - scrollable */}
        <div className="flex-1 overflow-y-auto px-4 pb-6 min-h-0">{children}</div>
      </div>
    </div>
  );
}
